// src/screens/AdminStreamerOutreachScreen.js
// ============================================================================
// ADMIN STREAMER OUTREACH — Cold email campaign management
//
// Tabs: Discover | Leads | Sent | Replied
//
// Flow: Run a YouTube discovery query -> leads appear -> add email manually ->
// generate Claude Haiku draft -> review/edit -> send via SendGrid from
// Dennis.Kreger@critcash.com -> track opens/clicks via webhook -> manually
// mark replied when a reply lands in Outlook.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView,
  TouchableOpacity, ActivityIndicator, RefreshControl,
  Modal, TextInput, Platform, Linking,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useError } from '../components/ErrorContext';

// ----------------------------------------------------------------------------
// CONSTANTS
// ----------------------------------------------------------------------------

const COLORS = {
  GOLD: '#FCD000',
  BG: '#0b0b0b',
  CARD: '#101010',
  CARD2: '#161616',
  TEXT: '#EDEDED',
  DIM: '#A0A0A0',
  BORDER: '#1a1a1a',
  SUCCESS: '#2ECC71',
  DANGER: '#E74C3C',
  WARN: '#F39C12',
  BLUE: '#3498DB',
  PURPLE: '#9146FF',
};

const TABS = ['Discover', 'Leads', 'Sent', 'Replied'];

const STATUS = {
  DISCOVERED: 'discovered',
  EMAIL_ADDED: 'email_added',
  DRAFTED: 'drafted',
  SENT: 'sent',
  OPENED: 'opened',
  CLICKED: 'clicked',
  REPLIED: 'replied',
  CONVERTED: 'converted',
  BOUNCED: 'bounced',
  REJECTED: 'rejected',
  ARCHIVED: 'archived',
};

const STATUS_COLORS = {
  [STATUS.DISCOVERED]: COLORS.DIM,
  [STATUS.EMAIL_ADDED]: COLORS.BLUE,
  [STATUS.DRAFTED]: COLORS.PURPLE,
  [STATUS.SENT]: COLORS.GOLD,
  [STATUS.OPENED]: COLORS.WARN,
  [STATUS.CLICKED]: COLORS.WARN,
  [STATUS.REPLIED]: COLORS.SUCCESS,
  [STATUS.CONVERTED]: COLORS.SUCCESS,
  [STATUS.BOUNCED]: COLORS.DANGER,
  [STATUS.REJECTED]: COLORS.DANGER,
  [STATUS.ARCHIVED]: COLORS.DIM,
};

const SORT_OPTIONS = [
  { value: 'apostle_desc', label: 'Apostle ↓' },
  { value: 'activity_desc', label: 'Activity ↓' },
  { value: 'most_engaged', label: 'Most Engaged ↓' },
  { value: 'recent_upload', label: 'Last Upload ↓' },
  { value: 'recent', label: 'Added' },
  { value: 'subs_desc', label: 'Subs ↓' },
  { value: 'subs_asc', label: 'Subs ↑' },
  { value: 'views_desc', label: 'Views ↓' },
  { value: 'views_asc', label: 'Views ↑' },
  { value: 'videos_desc', label: 'Videos ↓' },
  { value: 'videos_asc', label: 'Videos ↑' },
];

const ENGAGEMENT_TYPES = [
  { value: 'comment_video',      label: 'Comment on Video' },
  { value: 'comment_live',       label: 'Comment on Live' },
  { value: 'liked_video',        label: 'Liked Video(s)' },
  { value: 'replied_to_comment', label: 'Replied to Their Comment' },
  { value: 'watched_live',       label: 'Watched Live Stream' },
  { value: 'joined_discord',     label: 'Joined Their Discord' },
  { value: 'dm_youtube',         label: 'DM on YouTube' },
  { value: 'dm_social',          label: 'DM on Social (X/IG/etc)' },
  { value: 'shared_mention',     label: 'Shared / Mentioned' },
  { value: 'meetup',             label: 'In-Person Meetup' },
  { value: 'call',               label: 'Phone / Video Call' },
  { value: 'other',              label: 'Other' },
];
const ENGAGEMENT_TYPE_LABEL = Object.fromEntries(ENGAGEMENT_TYPES.map((t) => [t.value, t.label]));
const VIDEO_ENGAGEMENT_TYPES = new Set(['comment_video', 'comment_live', 'liked_video', 'replied_to_comment', 'watched_live']);

const TIER_COLORS = {
  hot:      '#E74C3C', // red-orange — fresh + frequent
  active:   '#F39C12', // amber
  moderate: '#3498DB', // blue
  cold:     '#7F8C8D', // grey
  dead:     '#4A4A4A', // dim
};

const TIER_FILTERS = [
  { value: '', label: 'All' },
  { value: 'hot', label: 'Hot' },
  { value: 'active', label: 'Active' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'cold', label: 'Cold' },
  { value: 'dead', label: 'Dead' },
];

const REGION_FILTERS = [
  { value: '', label: 'All' },
  { value: 'Americas', label: 'Americas' },
  { value: 'Europe', label: 'Europe' },
  { value: 'Asia', label: 'Asia' },
  { value: 'Oceania', label: 'Oceania' },
  { value: 'Middle East', label: 'Middle East' },
  { value: 'Africa', label: 'Africa' },
  { value: 'Unknown', label: 'Unknown' },
];

const LANGUAGE_LABEL = {
  en: 'English', es: 'Spanish', pt: 'Portuguese', de: 'German', fr: 'French',
  it: 'Italian', nl: 'Dutch', pl: 'Polish', ja: 'Japanese', ko: 'Korean',
  ru: 'Russian', zh: 'Chinese', ar: 'Arabic', th: 'Thai', el: 'Greek',
  he: 'Hebrew', unknown: 'Unknown',
};

const LANGUAGE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pl', label: 'Polish' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ru', label: 'Russian' },
  { value: 'unknown', label: 'Unknown' },
];

// Render an ISO 3166-1 alpha-2 country code as a flag emoji
// using Unicode regional indicator symbols (U+1F1E6 + (letter - 'A')).
function flagEmoji(cc) {
  if (!cc || typeof cc !== 'string' || cc.length !== 2) return '';
  const base = 0x1F1E6;
  const A = 'A'.charCodeAt(0);
  const up = cc.toUpperCase();
  if (!/^[A-Z]{2}$/.test(up)) return '';
  return String.fromCodePoint(base + up.charCodeAt(0) - A) + String.fromCodePoint(base + up.charCodeAt(1) - A);
}

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: STATUS.DISCOVERED, label: 'Discovered' },
  { value: STATUS.EMAIL_ADDED, label: 'Email Added' },
  { value: STATUS.DRAFTED, label: 'Drafted' },
  { value: STATUS.SENT, label: 'Sent' },
  { value: STATUS.OPENED, label: 'Opened' },
  { value: STATUS.CLICKED, label: 'Clicked' },
  { value: STATUS.REPLIED, label: 'Replied' },
  { value: STATUS.BOUNCED, label: 'Bounced' },
  { value: STATUS.ARCHIVED, label: 'Archived' },
];

const SENT_STATUSES = new Set([
  STATUS.SENT, STATUS.OPENED, STATUS.CLICKED, STATUS.REPLIED,
  STATUS.CONVERTED, STATUS.BOUNCED,
]);

// ----------------------------------------------------------------------------
// HELPERS
// ----------------------------------------------------------------------------

const functions = getFunctions();

function formatNumber(n) {
  if (n == null) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString();
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function useDebounced(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// ----------------------------------------------------------------------------
// MAIN SCREEN
// ----------------------------------------------------------------------------

export default function AdminStreamerOutreachScreen({ navigation }) {
  const { user, isAdmin, isLoading } = useAdminAuth();
  const { showError } = useError();
  const [activeTab, setActiveTab] = useState('Discover');

  // Shared leads state (used across Leads/Sent/Replied tabs)
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('apostle_desc');
  const [tierFilter, setTierFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const search = useDebounced(searchInput, 250);
  const [backfilling, setBackfilling] = useState(false);

  // Modal state
  const [emailModal, setEmailModal] = useState(null); // { lead }
  const [emailInput, setEmailInput] = useState('');
  const [draftModal, setDraftModal] = useState(null); // { lead }
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyModal, setReplyModal] = useState(null); // { lead }
  const [replyNotes, setReplyNotes] = useState('');

  // Engagement log / timeline
  const [logModal, setLogModal] = useState(null); // { lead }
  const [logType, setLogType] = useState('comment_video');
  const [logNote, setLogNote] = useState('');
  const [logVideoTitle, setLogVideoTitle] = useState('');
  const [logVideoUrl, setLogVideoUrl] = useState('');
  const [timelineModal, setTimelineModal] = useState(null); // { lead }
  const [timelineItems, setTimelineItems] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Discover tab state
  const [discoverQuery, setDiscoverQuery] = useState('');
  const [discoverMax, setDiscoverMax] = useState('25');
  const [discovering, setDiscovering] = useState(false);
  const [lastDiscoveryResult, setLastDiscoveryResult] = useState(null);

  const loadLeads = useCallback(async (reset = true) => {
    if (!isAdmin) return;
    setLoadingLeads(true);
    try {
      const call = httpsCallable(functions, 'adminListLeads');
      const res = await call({
        status: statusFilter || undefined,
        limit: 100,
        cursor: reset ? null : cursor,
      });
      const data = res.data || {};
      const incoming = data.leads || [];
      setLeads((prev) => (reset ? incoming : [...prev, ...incoming]));
      setCursor(reset ? null : cursor);
      setNextCursor(data.nextCursor || null);
    } catch (err) {
      showError('Failed to load leads', err?.message || String(err));
    } finally {
      setLoadingLeads(false);
    }
  }, [isAdmin, statusFilter, cursor, showError]);

  useEffect(() => {
    if (isAdmin) loadLeads(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, statusFilter]);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const call = httpsCallable(functions, 'adminBackfillYoutubeLeads');
      const res = await call({});
      const { enriched = 0, checked = 0 } = res.data || {};
      await loadLeads(true);
      showError('Backfill complete', `Enriched ${enriched} of ${checked} leads with @handle + latest stats.`);
    } catch (err) {
      showError('Backfill failed', err?.message || String(err));
    } finally {
      setBackfilling(false);
    }
  };

  const runDiscovery = async () => {
    if (!discoverQuery.trim()) {
      showError('Query required', 'Enter a search term like "iRacing highlights".');
      return;
    }
    setDiscovering(true);
    setLastDiscoveryResult(null);
    try {
      const call = httpsCallable(functions, 'adminDiscoverYoutubeStreamers');
      const res = await call({
        query: discoverQuery.trim(),
        maxResults: Math.min(50, Math.max(1, parseInt(discoverMax, 10) || 25)),
      });
      setLastDiscoveryResult(res.data || {});
      await loadLeads(true);
    } catch (err) {
      showError('Discovery failed', err?.message || String(err));
    } finally {
      setDiscovering(false);
    }
  };

  const openEmailModal = (lead) => {
    setEmailInput(lead.email || '');
    setEmailModal({ lead });
  };

  const submitEmail = async () => {
    if (!emailModal) return;
    try {
      const call = httpsCallable(functions, 'adminSetLeadEmail');
      await call({ leadId: emailModal.lead.id, email: emailInput.trim() });
      setEmailModal(null);
      await loadLeads(true);
    } catch (err) {
      showError('Failed to save email', err?.message || String(err));
    }
  };

  const openDraftModal = async (lead, autogen = false) => {
    setDraftModal({ lead });
    setDraftSubject(lead.draftSubject || '');
    setDraftBody(lead.draftBody || '');
    if (autogen || (!lead.draftSubject && !lead.draftBody)) {
      await generateDraft(lead);
    }
  };

  const generateDraft = async (lead) => {
    const target = lead || draftModal?.lead;
    if (!target) return;
    setGeneratingDraft(true);
    try {
      const call = httpsCallable(functions, 'adminGenerateOutreachDraft');
      const res = await call({ leadId: target.id });
      const { subject, body } = res.data || {};
      setDraftSubject(subject || '');
      setDraftBody(body || '');
    } catch (err) {
      showError('Draft generation failed', err?.message || String(err));
    } finally {
      setGeneratingDraft(false);
    }
  };

  const saveDraft = async () => {
    if (!draftModal) return;
    try {
      const call = httpsCallable(functions, 'adminUpdateOutreachDraft');
      await call({
        leadId: draftModal.lead.id,
        subject: draftSubject,
        body: draftBody,
      });
      await loadLeads(true);
    } catch (err) {
      showError('Failed to save draft', err?.message || String(err));
    }
  };

  const sendNow = async () => {
    if (!draftModal) return;
    if (!draftModal.lead.email) {
      showError('No email', 'Add an email for this lead before sending.');
      return;
    }
    if (!draftSubject.trim() || !draftBody.trim()) {
      showError('Draft incomplete', 'Subject and body are both required.');
      return;
    }
    setSending(true);
    try {
      // Save latest edits first
      const save = httpsCallable(functions, 'adminUpdateOutreachDraft');
      await save({
        leadId: draftModal.lead.id,
        subject: draftSubject,
        body: draftBody,
      });
      const send = httpsCallable(functions, 'adminSendOutreachEmail');
      await send({ leadId: draftModal.lead.id });
      setDraftModal(null);
      await loadLeads(true);
    } catch (err) {
      showError('Send failed', err?.message || String(err));
    } finally {
      setSending(false);
    }
  };

  const submitReply = async () => {
    if (!replyModal) return;
    try {
      const call = httpsCallable(functions, 'adminMarkLeadReplied');
      await call({ leadId: replyModal.lead.id, notes: replyNotes });
      setReplyModal(null);
      setReplyNotes('');
      await loadLeads(true);
    } catch (err) {
      showError('Failed to mark replied', err?.message || String(err));
    }
  };

  const openLogModal = (lead) => {
    setLogType('comment_video');
    setLogNote('');
    setLogVideoTitle('');
    setLogVideoUrl('');
    setLogModal({ lead });
  };

  const submitLog = async () => {
    if (!logModal) return;
    if (!logNote.trim() && !logVideoUrl.trim()) {
      showError('Nothing to log', 'Add a note or a video URL before saving.');
      return;
    }
    try {
      const call = httpsCallable(functions, 'adminLogEngagement');
      await call({
        leadId: logModal.lead.id,
        type: logType,
        note: logNote,
        videoTitle: logVideoTitle,
        videoUrl: logVideoUrl,
      });
      setLogModal(null);
      await loadLeads(true);
    } catch (err) {
      showError('Log failed', err?.message || String(err));
    }
  };

  const openTimeline = async (lead) => {
    setTimelineModal({ lead });
    setTimelineItems([]);
    setTimelineLoading(true);
    try {
      const call = httpsCallable(functions, 'adminListEngagements');
      const res = await call({ leadId: lead.id, limit: 200 });
      setTimelineItems(res.data?.engagements || []);
    } catch (err) {
      showError('Failed to load timeline', err?.message || String(err));
    } finally {
      setTimelineLoading(false);
    }
  };

  const deleteEngagement = async (engagementId) => {
    if (!timelineModal) return;
    try {
      const call = httpsCallable(functions, 'adminDeleteEngagement');
      await call({ leadId: timelineModal.lead.id, engagementId });
      setTimelineItems((prev) => prev.filter((e) => e.id !== engagementId));
      await loadLeads(true);
    } catch (err) {
      showError('Delete failed', err?.message || String(err));
    }
  };

  const archiveLead = async (lead) => {
    try {
      const call = httpsCallable(functions, 'adminArchiveLead');
      await call({ leadId: lead.id, reason: 'Archived from admin UI' });
      await loadLeads(true);
    } catch (err) {
      showError('Archive failed', err?.message || String(err));
    }
  };

  // Filter leads client-side by search query
  const filteredLeads = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = leads;
    if (activeTab === 'Sent') {
      list = list.filter((l) => SENT_STATUSES.has(l.status) && l.status !== STATUS.REPLIED);
    } else if (activeTab === 'Replied') {
      list = list.filter((l) => l.status === STATUS.REPLIED || l.status === STATUS.CONVERTED);
    }
    if (term) {
      list = list.filter((l) => {
        const hay = `${l.channelName || ''} ${l.channelHandle || ''} ${l.email || ''} ${(l.gameFocus || []).join(' ')}`.toLowerCase();
        return hay.includes(term);
      });
    }
    if (tierFilter) {
      list = list.filter((l) => (l.activityTier || 'dead') === tierFilter);
    }
    if (regionFilter) {
      list = list.filter((l) => (l.region || 'Unknown') === regionFilter);
    }
    if (languageFilter) {
      list = list.filter((l) => (l.language || 'unknown') === languageFilter);
    }
    // Sort
    const sorted = [...list];
    const cmp = (a, b, key) => (Number(a[key] || 0) - Number(b[key] || 0));
    const lastUploadMs = (l) => {
      const v = l.lastUploadAt;
      if (!v) return 0;
      if (v.toMillis) return v.toMillis();
      if (v._seconds) return v._seconds * 1000;
      const d = new Date(v);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    };
    switch (sortBy) {
      case 'apostle_desc':  sorted.sort((a, b) => cmp(b, a, 'apostleScore')); break;
      case 'activity_desc': sorted.sort((a, b) => cmp(b, a, 'activityScore')); break;
      case 'most_engaged':  sorted.sort((a, b) => cmp(b, a, 'engagementCount')); break;
      case 'recent_upload': sorted.sort((a, b) => lastUploadMs(b) - lastUploadMs(a)); break;
      case 'subs_desc':     sorted.sort((a, b) => cmp(b, a, 'subscriberCount')); break;
      case 'subs_asc':      sorted.sort((a, b) => cmp(a, b, 'subscriberCount')); break;
      case 'views_desc':    sorted.sort((a, b) => cmp(b, a, 'viewCount')); break;
      case 'views_asc':     sorted.sort((a, b) => cmp(a, b, 'viewCount')); break;
      case 'videos_desc':   sorted.sort((a, b) => cmp(b, a, 'videoCount')); break;
      case 'videos_asc':    sorted.sort((a, b) => cmp(a, b, 'videoCount')); break;
      default: break; // 'recent' — preserve server order (createdAt desc)
    }
    return sorted;
  }, [leads, search, activeTab, sortBy, tierFilter, regionFilter, languageFilter]);

  if (isLoading || !user) {
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.center}>
          <ActivityIndicator color={COLORS.GOLD} />
        </View>
      </SafeAreaView>
    );
  }
  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.center}>
          <Text style={styles.deniedText}>Access Denied</Text>
          <TouchableOpacity onPress={() => navigation.navigate('Lobby')}>
            <Text style={styles.backLink}>Go to Lobby</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />
      <ScrollView
        contentContainerStyle={styles.container}
        refreshControl={
          <RefreshControl
            refreshing={loadingLeads}
            onRefresh={() => loadLeads(true)}
            tintColor={COLORS.GOLD}
          />
        }
      >
        <View style={styles.hero}>
          <Text style={styles.title}>Streamer Outreach</Text>
          <Text style={styles.subtitle}>YouTube racing-game creator cold email</Text>
        </View>

        {/* Tabs */}
        <View style={styles.tabs}>
          {TABS.map((t) => (
            <TouchableOpacity
              key={t}
              onPress={() => setActiveTab(t)}
              style={[styles.tab, activeTab === t && styles.tabActive]}
            >
              <Text style={[styles.tabText, activeTab === t && styles.tabTextActive]}>{t}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Discover tab */}
        {activeTab === 'Discover' && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Run YouTube Discovery</Text>
            <Text style={styles.dim}>
              Search YouTube for racing-game creators. New channels are upserted into leads.
              Existing channels are skipped (dedup by channel ID).
            </Text>

            <Text style={styles.label}>Search query</Text>
            <TextInput
              style={styles.input}
              placeholder='e.g. "iRacing highlights", "Assetto Corsa setup tutorial"'
              placeholderTextColor={COLORS.DIM}
              value={discoverQuery}
              onChangeText={setDiscoverQuery}
              onSubmitEditing={runDiscovery}
            />

            <Text style={styles.label}>Max results (1-50)</Text>
            <TextInput
              style={styles.input}
              keyboardType="numeric"
              value={String(discoverMax)}
              onChangeText={setDiscoverMax}
            />

            <TouchableOpacity
              style={[styles.primaryBtn, discovering && styles.btnDisabled]}
              onPress={runDiscovery}
              disabled={discovering}
            >
              {discovering
                ? <ActivityIndicator color="#000" />
                : <Text style={styles.primaryBtnText}>Run Discovery</Text>
              }
            </TouchableOpacity>

            {lastDiscoveryResult && (
              <View style={styles.resultBox}>
                <Text style={styles.resultTitle}>
                  Discovered {lastDiscoveryResult.discovered || 0} new · Skipped {lastDiscoveryResult.skipped || 0}
                </Text>
                {(lastDiscoveryResult.details || []).slice(0, 10).map((d) => (
                  <Text key={d.leadId} style={styles.resultLine} numberOfLines={1}>
                    + {d.channelName} · {formatNumber(d.subscriberCount)} subs · Apostle {d.apostleScore ?? '?'} · {(d.activityTier || 'unknown').toUpperCase()} · {(d.gameFocus || []).join(', ') || 'no tags'}
                  </Text>
                ))}
              </View>
            )}

            <View style={styles.divider} />

            <Text style={styles.cardTitle}>Enrich Existing Leads</Text>
            <Text style={styles.dim}>
              Fill in missing @handles and refresh stats for leads discovered before the @handle feature was added.
              One-time per lead; subsequent clicks are cheap no-ops.
            </Text>
            <TouchableOpacity
              style={[styles.secondaryBtn, backfilling && styles.btnDisabled]}
              onPress={runBackfill}
              disabled={backfilling}
            >
              {backfilling
                ? <ActivityIndicator color={COLORS.TEXT} />
                : <Text style={styles.secondaryBtnText}>Backfill existing leads</Text>
              }
            </TouchableOpacity>
          </View>
        )}

        {/* Filter bar — shown on non-Discover tabs */}
        {activeTab !== 'Discover' && (
          <View style={styles.filterBar}>
            <TextInput
              style={[styles.input, styles.filterInput]}
              placeholder="Search by channel, @handle, email, or game…"
              placeholderTextColor={COLORS.DIM}
              value={searchInput}
              onChangeText={setSearchInput}
            />
            {activeTab === 'Leads' && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsRow}>
                {STATUS_FILTERS.map((s) => (
                  <TouchableOpacity
                    key={s.value || 'all'}
                    style={[styles.chip, statusFilter === s.value && styles.chipActive]}
                    onPress={() => setStatusFilter(s.value)}
                  >
                    <Text style={[styles.chipText, statusFilter === s.value && styles.chipTextActive]}>
                      {s.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            {activeTab === 'Leads' && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsRow}>
                <Text style={styles.sortLabel}>Activity:</Text>
                {TIER_FILTERS.map((t) => (
                  <TouchableOpacity
                    key={t.value || 'all'}
                    style={[
                      styles.chip,
                      tierFilter === t.value && styles.chipActive,
                      tierFilter === t.value && t.value && { backgroundColor: TIER_COLORS[t.value], borderColor: TIER_COLORS[t.value] },
                    ]}
                    onPress={() => setTierFilter(t.value)}
                  >
                    <Text style={[styles.chipText, tierFilter === t.value && styles.chipTextActive]}>
                      {t.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            {activeTab === 'Leads' && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsRow}>
                <Text style={styles.sortLabel}>Region:</Text>
                {REGION_FILTERS.map((r) => (
                  <TouchableOpacity
                    key={r.value || 'all'}
                    style={[styles.chip, regionFilter === r.value && styles.chipActive]}
                    onPress={() => setRegionFilter(r.value)}
                  >
                    <Text style={[styles.chipText, regionFilter === r.value && styles.chipTextActive]}>
                      {r.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            {activeTab === 'Leads' && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsRow}>
                <Text style={styles.sortLabel}>Language:</Text>
                {LANGUAGE_FILTERS.map((l) => (
                  <TouchableOpacity
                    key={l.value || 'all'}
                    style={[styles.chip, languageFilter === l.value && styles.chipActive]}
                    onPress={() => setLanguageFilter(l.value)}
                  >
                    <Text style={[styles.chipText, languageFilter === l.value && styles.chipTextActive]}>
                      {l.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsRow}>
              <Text style={styles.sortLabel}>Sort:</Text>
              {SORT_OPTIONS.map((s) => (
                <TouchableOpacity
                  key={s.value}
                  style={[styles.chip, sortBy === s.value && styles.chipActive]}
                  onPress={() => setSortBy(s.value)}
                >
                  <Text style={[styles.chipText, sortBy === s.value && styles.chipTextActive]}>
                    {s.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* Leads / Sent / Replied list */}
        {activeTab !== 'Discover' && (
          <View style={{ marginTop: 12 }}>
            {loadingLeads && <ActivityIndicator color={COLORS.GOLD} style={{ marginTop: 12 }} />}
            {!loadingLeads && filteredLeads.length === 0 && (
              <View style={styles.empty}>
                <Text style={styles.dim}>No leads match this view. Run a discovery query on the Discover tab.</Text>
              </View>
            )}
            {filteredLeads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                tab={activeTab}
                onAddEmail={() => openEmailModal(lead)}
                onGenerateDraft={() => openDraftModal(lead, true)}
                onReviewDraft={() => openDraftModal(lead, false)}
                onMarkReplied={() => setReplyModal({ lead })}
                onArchive={() => archiveLead(lead)}
                onLogEngagement={() => openLogModal(lead)}
                onViewTimeline={() => openTimeline(lead)}
              />
            ))}
            {nextCursor && activeTab === 'Leads' && !loadingLeads && (
              <TouchableOpacity
                style={styles.secondaryBtn}
                onPress={() => { setCursor(nextCursor); loadLeads(false); }}
              >
                <Text style={styles.secondaryBtnText}>Load more</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </ScrollView>

      {/* Email entry modal */}
      <Modal transparent visible={!!emailModal} animationType="fade" onRequestClose={() => setEmailModal(null)}>
        <View style={styles.modalWrap}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Add email for {emailModal?.lead?.channelName}</Text>
            <Text style={styles.dim} numberOfLines={1}>{emailModal?.lead?.channelUrl}</Text>
            <TextInput
              style={styles.input}
              placeholder="streamer@example.com"
              placeholderTextColor={COLORS.DIM}
              value={emailInput}
              onChangeText={setEmailInput}
              autoCapitalize="none"
              keyboardType="email-address"
            />
            <View style={styles.modalBtnRow}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setEmailModal(null)}>
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.primaryBtn} onPress={submitEmail}>
                <Text style={styles.primaryBtnText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Draft review modal */}
      <Modal transparent visible={!!draftModal} animationType="fade" onRequestClose={() => setDraftModal(null)}>
        <View style={styles.modalWrap}>
          <View style={[styles.modalCard, styles.modalCardWide]}>
            <Text style={styles.modalTitle}>Review draft — {draftModal?.lead?.channelName}</Text>
            <Text style={styles.dim} numberOfLines={1}>
              To: {draftModal?.lead?.email || '(no email)'}  ·  From: Dennis.Kreger@critcash.com
            </Text>

            <Text style={styles.label}>Subject</Text>
            <TextInput
              style={styles.input}
              value={draftSubject}
              onChangeText={setDraftSubject}
              placeholder="Subject line"
              placeholderTextColor={COLORS.DIM}
            />

            <Text style={styles.label}>Body (HTML)</Text>
            <TextInput
              style={[styles.input, styles.textarea]}
              value={draftBody}
              onChangeText={setDraftBody}
              placeholder="Generating…"
              placeholderTextColor={COLORS.DIM}
              multiline
            />

            {generatingDraft && (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 6 }}>
                <ActivityIndicator color={COLORS.GOLD} />
                <Text style={[styles.dim, { marginLeft: 8 }]}>Claude Haiku is drafting…</Text>
              </View>
            )}

            <View style={styles.modalBtnRow}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => generateDraft()}>
                <Text style={styles.secondaryBtnText}>Regenerate</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.secondaryBtn} onPress={saveDraft}>
                <Text style={styles.secondaryBtnText}>Save draft</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.primaryBtn, sending && styles.btnDisabled]}
                onPress={sendNow}
                disabled={sending}
              >
                {sending
                  ? <ActivityIndicator color="#000" />
                  : <Text style={styles.primaryBtnText}>Send now</Text>
                }
              </TouchableOpacity>
            </View>

            <TouchableOpacity style={styles.closeBtn} onPress={() => setDraftModal(null)}>
              <Text style={styles.dim}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Mark replied modal */}
      <Modal transparent visible={!!replyModal} animationType="fade" onRequestClose={() => setReplyModal(null)}>
        <View style={styles.modalWrap}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Mark replied — {replyModal?.lead?.channelName}</Text>
            <Text style={styles.dim}>Paste a summary of their reply (optional).</Text>
            <TextInput
              style={[styles.input, styles.textarea]}
              value={replyNotes}
              onChangeText={setReplyNotes}
              placeholder="e.g. Interested, wants to schedule a call next week"
              placeholderTextColor={COLORS.DIM}
              multiline
            />
            <View style={styles.modalBtnRow}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setReplyModal(null)}>
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.primaryBtn} onPress={submitReply}>
                <Text style={styles.primaryBtnText}>Mark replied</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Log Engagement modal */}
      <Modal transparent visible={!!logModal} animationType="fade" onRequestClose={() => setLogModal(null)}>
        <View style={styles.modalWrap}>
          <View style={[styles.modalCard, styles.modalCardWide]}>
            <Text style={styles.modalTitle}>Log engagement — {logModal?.lead?.channelName}</Text>
            <Text style={styles.dim}>Record what you did in their community (comment, like, DM, etc.).</Text>

            <Text style={styles.label}>Type</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4, flexGrow: 0 }}>
              {ENGAGEMENT_TYPES.map((t) => (
                <TouchableOpacity
                  key={t.value}
                  style={[styles.chip, logType === t.value && styles.chipActive]}
                  onPress={() => setLogType(t.value)}
                >
                  <Text style={[styles.chipText, logType === t.value && styles.chipTextActive]}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <Text style={styles.label}>Note — what did you say / what happened?</Text>
            <TextInput
              style={[styles.input, styles.textarea]}
              value={logNote}
              onChangeText={setLogNote}
              placeholder='e.g. "Clean overtake in turn 6 — that apex was surgical."'
              placeholderTextColor={COLORS.DIM}
              multiline
            />

            {VIDEO_ENGAGEMENT_TYPES.has(logType) && (
              <>
                <Text style={styles.label}>Video title (optional)</Text>
                <TextInput
                  style={styles.input}
                  value={logVideoTitle}
                  onChangeText={setLogVideoTitle}
                  placeholder="GT7 Daily Race C — Tokyo Expressway"
                  placeholderTextColor={COLORS.DIM}
                />
                <Text style={styles.label}>Video URL (optional)</Text>
                <TextInput
                  style={styles.input}
                  value={logVideoUrl}
                  onChangeText={setLogVideoUrl}
                  placeholder="https://www.youtube.com/watch?v=..."
                  placeholderTextColor={COLORS.DIM}
                  autoCapitalize="none"
                />
              </>
            )}

            <View style={styles.modalBtnRow}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setLogModal(null)}>
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.primaryBtn} onPress={submitLog}>
                <Text style={styles.primaryBtnText}>Log it</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Timeline modal */}
      <Modal transparent visible={!!timelineModal} animationType="fade" onRequestClose={() => setTimelineModal(null)}>
        <View style={styles.modalWrap}>
          <View style={[styles.modalCard, styles.modalCardWide]}>
            <Text style={styles.modalTitle}>Timeline — {timelineModal?.lead?.channelName}</Text>
            <Text style={styles.dim}>All community engagement, most recent first.</Text>

            <ScrollView style={{ maxHeight: 420, marginTop: 12 }}>
              {timelineLoading && <ActivityIndicator color={COLORS.GOLD} />}
              {!timelineLoading && timelineItems.length === 0 && (
                <Text style={styles.dim}>No engagements logged yet.</Text>
              )}
              {timelineItems.map((e) => (
                <View key={e.id} style={styles.timelineRow}>
                  <View style={{ flex: 1 }}>
                    <View style={styles.timelineHead}>
                      <View style={styles.typePill}>
                        <Text style={styles.typePillText}>{ENGAGEMENT_TYPE_LABEL[e.type] || e.type}</Text>
                      </View>
                      <Text style={styles.dim}>{formatDate(e.at)}</Text>
                    </View>
                    {e.note && <Text style={styles.timelineNote}>{e.note}</Text>}
                    {e.videoTitle && <Text style={styles.dim} numberOfLines={1}>🎬 {e.videoTitle}</Text>}
                    {e.videoUrl && (
                      <TouchableOpacity onPress={() => {
                        if (Platform.OS === 'web') window.open(e.videoUrl, '_blank', 'noopener,noreferrer');
                        else Linking.openURL(e.videoUrl);
                      }}>
                        <Text style={styles.timelineLink} numberOfLines={1}>{e.videoUrl}</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  <TouchableOpacity onPress={() => deleteEngagement(e.id)} style={styles.timelineDelete}>
                    <Text style={styles.timelineDeleteText}>×</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>

            <View style={styles.modalBtnRow}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setTimelineModal(null)}>
                <Text style={styles.secondaryBtnText}>Close</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.primaryBtn}
                onPress={() => {
                  const lead = timelineModal?.lead;
                  setTimelineModal(null);
                  if (lead) openLogModal(lead);
                }}
              >
                <Text style={styles.primaryBtnText}>+ Add engagement</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Footer />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ----------------------------------------------------------------------------
// LEAD CARD
// ----------------------------------------------------------------------------
function LeadCard({ lead, tab, onAddEmail, onGenerateDraft, onReviewDraft, onMarkReplied, onArchive, onLogEngagement, onViewTimeline }) {
  const hasEmail = !!lead.email;
  const hasDraft = !!(lead.draftSubject && lead.draftBody);
  const isSent = SENT_STATUSES.has(lead.status);
  const isReplied = lead.status === STATUS.REPLIED || lead.status === STATUS.CONVERTED;
  const engagement = lead.engagement || {};
  const engCount = Number(lead.engagementCount || 0);

  return (
    <View style={styles.leadCard}>
      <View style={styles.leadHead}>
        <View style={{ flex: 1 }}>
          <TouchableOpacity
            onPress={() => {
              if (!lead.channelUrl) return;
              if (Platform.OS === 'web') {
                window.open(lead.channelUrl, '_blank', 'noopener,noreferrer');
              } else {
                Linking.openURL(lead.channelUrl);
              }
            }}
            disabled={!lead.channelUrl}
          >
            <Text style={styles.leadName} numberOfLines={1}>
              {lead.channelName}
              {lead.channelHandle ? <Text style={styles.handleInline}>  {lead.channelHandle}</Text> : null}
              {' '}<Text style={styles.leadLink}>↗</Text>
            </Text>
          </TouchableOpacity>
          <Text style={styles.dim} numberOfLines={1}>
            {formatNumber(lead.subscriberCount)} subs · {formatNumber(lead.viewCount)} views · {lead.videoCount || 0} videos
          </Text>
          {(lead.uploadsPerWeek != null || lead.daysSinceLastUpload != null) && (
            <Text style={styles.dim} numberOfLines={1}>
              {lead.uploadsPerWeek ? `${lead.uploadsPerWeek}/week` : 'cadence unknown'}
              {lead.daysSinceLastUpload != null
                ? ` · last upload ${lead.daysSinceLastUpload === 0 ? 'today' : `${lead.daysSinceLastUpload}d ago`}`
                : ''}
            </Text>
          )}
          {(lead.country || lead.region || lead.language) && (
            <Text style={styles.dim} numberOfLines={1}>
              {flagEmoji(lead.country)} {lead.country || '??'} · {lead.region || 'Unknown'}
              {lead.language ? ` · ${LANGUAGE_LABEL[lead.language] || lead.language}` : ''}
            </Text>
          )}
          {(lead.gameFocus || []).length > 0 && (
            <View style={styles.tagRow}>
              {lead.gameFocus.slice(0, 4).map((g) => (
                <View key={g} style={styles.tag}>
                  <Text style={styles.tagText}>{g}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          {typeof lead.apostleScore === 'number' && (
            <View style={styles.apostleBadge}>
              <Text style={styles.apostleBadgeLabel}>APOSTLE</Text>
              <Text style={styles.apostleBadgeScore}>{lead.apostleScore}</Text>
            </View>
          )}
          {lead.activityTier && (
            <View style={[styles.tierPill, { backgroundColor: TIER_COLORS[lead.activityTier] || COLORS.DIM }]}>
              <Text style={styles.tierPillText}>{String(lead.activityTier).toUpperCase()}</Text>
            </View>
          )}
          <View style={[styles.statusPill, { backgroundColor: STATUS_COLORS[lead.status] || COLORS.DIM }]}>
            <Text style={styles.statusPillText}>{lead.status}</Text>
          </View>
        </View>
      </View>

      <View style={styles.leadMeta}>
        <Text style={styles.dim} numberOfLines={1}>
          Email: {lead.email || <Text style={{ color: COLORS.WARN }}>not set</Text>}
        </Text>
        {lead.discoveredVia && (
          <Text style={styles.dim} numberOfLines={1}>Discovered via: "{lead.discoveredVia}"</Text>
        )}
        {engCount > 0 && (
          <Text style={styles.engagementLine} numberOfLines={1}>
            💬 {engCount} engagement{engCount === 1 ? '' : 's'}
            {lead.lastEngagementAt ? ` · last ${formatDate(lead.lastEngagementAt)}` : ''}
            {lead.lastEngagementType ? ` · ${ENGAGEMENT_TYPE_LABEL[lead.lastEngagementType] || lead.lastEngagementType}` : ''}
          </Text>
        )}
        {isSent && (
          <Text style={styles.dim}>
            Sent {formatDate(lead.sentAt)} · Opens {engagement.opens || 0} · Clicks {engagement.clicks || 0}
            {engagement.bounced ? ` · Bounced (${engagement.bounceType || 'unknown'})` : ''}
          </Text>
        )}
        {isReplied && lead.replyNotes && (
          <Text style={styles.replyNotes} numberOfLines={3}>Reply: {lead.replyNotes}</Text>
        )}
      </View>

      <View style={styles.actionsRow}>
        <TouchableOpacity style={[styles.actionBtn, styles.actionEngage]} onPress={onLogEngagement}>
          <Text style={styles.actionText}>+ Log</Text>
        </TouchableOpacity>
        {engCount > 0 && (
          <TouchableOpacity style={styles.actionBtn} onPress={onViewTimeline}>
            <Text style={styles.actionText}>Timeline ({engCount})</Text>
          </TouchableOpacity>
        )}
        {!hasEmail && (
          <TouchableOpacity style={styles.actionBtn} onPress={onAddEmail}>
            <Text style={styles.actionText}>Add Email</Text>
          </TouchableOpacity>
        )}
        {hasEmail && (
          <TouchableOpacity style={styles.actionBtn} onPress={onAddEmail}>
            <Text style={styles.actionText}>Edit Email</Text>
          </TouchableOpacity>
        )}
        {hasEmail && !isSent && !hasDraft && (
          <TouchableOpacity style={[styles.actionBtn, styles.actionPrimary]} onPress={onGenerateDraft}>
            <Text style={styles.actionText}>Generate Draft</Text>
          </TouchableOpacity>
        )}
        {hasEmail && !isSent && hasDraft && (
          <TouchableOpacity style={[styles.actionBtn, styles.actionPrimary]} onPress={onReviewDraft}>
            <Text style={styles.actionText}>Review & Send</Text>
          </TouchableOpacity>
        )}
        {isSent && !isReplied && (
          <TouchableOpacity style={[styles.actionBtn, styles.actionSuccess]} onPress={onMarkReplied}>
            <Text style={styles.actionText}>Mark Replied</Text>
          </TouchableOpacity>
        )}
        {lead.status !== STATUS.ARCHIVED && (
          <TouchableOpacity style={styles.actionBtn} onPress={onArchive}>
            <Text style={styles.actionTextDim}>Archive</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

// ----------------------------------------------------------------------------
// STYLES
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: COLORS.BG },
  container: { padding: 16, paddingBottom: 80 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  deniedText: { color: COLORS.DANGER, fontSize: 20, fontWeight: '700', marginBottom: 12 },
  backLink: { color: COLORS.GOLD, fontSize: 14 },

  hero: { marginBottom: 12 },
  title: { color: COLORS.TEXT, fontSize: 24, fontWeight: '800' },
  subtitle: { color: COLORS.DIM, fontSize: 14, marginTop: 4 },

  tabs: { flexDirection: 'row', marginBottom: 12, borderBottomWidth: 1, borderBottomColor: COLORS.BORDER },
  tab: { paddingVertical: 10, paddingHorizontal: 16 },
  tabActive: { borderBottomWidth: 2, borderBottomColor: COLORS.GOLD },
  tabText: { color: COLORS.DIM, fontSize: 14, fontWeight: '600' },
  tabTextActive: { color: COLORS.TEXT },

  card: {
    backgroundColor: COLORS.CARD, borderRadius: 10, padding: 16,
    borderWidth: 1, borderColor: COLORS.BORDER,
  },
  cardTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: '700', marginBottom: 8 },

  label: { color: COLORS.DIM, fontSize: 12, marginTop: 12, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: {
    backgroundColor: COLORS.CARD2, color: COLORS.TEXT, borderRadius: 8,
    borderWidth: 1, borderColor: COLORS.BORDER,
    paddingHorizontal: 12, paddingVertical: Platform.OS === 'web' ? 10 : 8,
    fontSize: 14, marginTop: 4,
  },
  textarea: { minHeight: 180, textAlignVertical: 'top' },

  primaryBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 8,
    paddingVertical: 12, paddingHorizontal: 16,
    alignItems: 'center', marginTop: 12, minWidth: 140,
  },
  primaryBtnText: { color: '#000', fontWeight: '800', fontSize: 14 },
  secondaryBtn: {
    backgroundColor: COLORS.CARD2, borderRadius: 8,
    paddingVertical: 10, paddingHorizontal: 14,
    alignItems: 'center', marginTop: 12,
    borderWidth: 1, borderColor: COLORS.BORDER,
  },
  secondaryBtnText: { color: COLORS.TEXT, fontWeight: '600', fontSize: 13 },
  btnDisabled: { opacity: 0.5 },

  resultBox: { marginTop: 16, padding: 12, backgroundColor: COLORS.CARD2, borderRadius: 8 },
  resultTitle: { color: COLORS.GOLD, fontWeight: '700', fontSize: 14, marginBottom: 6 },
  resultLine: { color: COLORS.DIM, fontSize: 12, marginVertical: 1 },

  filterBar: { marginTop: 4 },
  filterInput: { marginTop: 0 },
  chipsRow: { marginTop: 8, flexGrow: 0 },
  sortLabel: { color: COLORS.DIM, fontSize: 12, alignSelf: 'center', marginRight: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  divider: { height: 1, backgroundColor: COLORS.BORDER, marginVertical: 20 },
  chip: {
    paddingVertical: 6, paddingHorizontal: 12, backgroundColor: COLORS.CARD2,
    borderRadius: 16, marginRight: 8, borderWidth: 1, borderColor: COLORS.BORDER,
  },
  chipActive: { backgroundColor: COLORS.GOLD, borderColor: COLORS.GOLD },
  chipText: { color: COLORS.DIM, fontSize: 12 },
  chipTextActive: { color: '#000', fontWeight: '700' },

  empty: { padding: 20, alignItems: 'center' },
  dim: { color: COLORS.DIM, fontSize: 12 },

  leadCard: {
    backgroundColor: COLORS.CARD, borderRadius: 10, padding: 14,
    marginBottom: 10, borderWidth: 1, borderColor: COLORS.BORDER,
  },
  leadHead: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 },
  leadName: { color: COLORS.TEXT, fontSize: 15, fontWeight: '700' },
  leadLink: { color: COLORS.BLUE, fontSize: 12, fontWeight: '600' },
  handleInline: { color: COLORS.BLUE, fontSize: 13, fontWeight: '500' },
  leadMeta: { marginTop: 2, marginBottom: 10 },
  replyNotes: {
    color: COLORS.SUCCESS, fontSize: 12, marginTop: 6,
    padding: 8, backgroundColor: 'rgba(46,204,113,0.08)',
    borderRadius: 6, borderLeftWidth: 3, borderLeftColor: COLORS.SUCCESS,
  },

  tagRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 6 },
  tag: {
    backgroundColor: COLORS.CARD2, paddingVertical: 2, paddingHorizontal: 8,
    borderRadius: 10, marginRight: 6, marginBottom: 4,
  },
  tagText: { color: COLORS.DIM, fontSize: 10 },

  statusPill: { paddingVertical: 4, paddingHorizontal: 10, borderRadius: 12, marginLeft: 8, marginTop: 4 },
  statusPillText: { color: '#000', fontSize: 10, fontWeight: '800', textTransform: 'uppercase' },
  tierPill: { paddingVertical: 4, paddingHorizontal: 10, borderRadius: 12, marginLeft: 8, marginTop: 4 },
  tierPillText: { color: '#fff', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  apostleBadge: {
    backgroundColor: '#1a1a1a',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.GOLD,
    alignItems: 'center',
    minWidth: 72,
  },
  apostleBadgeLabel: { color: COLORS.GOLD, fontSize: 9, fontWeight: '700', letterSpacing: 1 },
  apostleBadgeScore: { color: COLORS.TEXT, fontSize: 20, fontWeight: '800', lineHeight: 22 },

  actionEngage: { backgroundColor: COLORS.PURPLE, borderColor: COLORS.PURPLE },
  engagementLine: {
    color: COLORS.PURPLE, fontSize: 12, marginTop: 2, fontWeight: '500',
  },
  timelineRow: {
    flexDirection: 'row', alignItems: 'flex-start',
    padding: 12, marginBottom: 8,
    backgroundColor: COLORS.CARD2, borderRadius: 8,
    borderLeftWidth: 3, borderLeftColor: COLORS.PURPLE,
  },
  timelineHead: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  typePill: {
    backgroundColor: COLORS.PURPLE, paddingVertical: 2, paddingHorizontal: 8,
    borderRadius: 10, marginRight: 8,
  },
  typePillText: { color: '#fff', fontSize: 10, fontWeight: '700' },
  timelineNote: { color: COLORS.TEXT, fontSize: 13, marginTop: 2, marginBottom: 4 },
  timelineLink: { color: COLORS.BLUE, fontSize: 11, marginTop: 2 },
  timelineDelete: { paddingHorizontal: 10, paddingVertical: 4 },
  timelineDeleteText: { color: COLORS.DIM, fontSize: 20, fontWeight: '700' },

  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 4 },
  actionBtn: {
    paddingVertical: 6, paddingHorizontal: 12, borderRadius: 6,
    backgroundColor: COLORS.CARD2, marginRight: 6, marginTop: 6,
    borderWidth: 1, borderColor: COLORS.BORDER,
  },
  actionPrimary: { backgroundColor: COLORS.GOLD, borderColor: COLORS.GOLD },
  actionSuccess: { backgroundColor: COLORS.SUCCESS, borderColor: COLORS.SUCCESS },
  actionText: { color: COLORS.TEXT, fontSize: 12, fontWeight: '600' },
  actionTextDim: { color: COLORS.DIM, fontSize: 12 },

  modalWrap: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.8)',
    alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modalCard: {
    backgroundColor: COLORS.CARD, borderRadius: 12, padding: 20,
    width: '100%', maxWidth: 520,
    borderWidth: 1, borderColor: COLORS.BORDER,
  },
  modalCardWide: { maxWidth: 720 },
  modalTitle: { color: COLORS.TEXT, fontWeight: '800', fontSize: 16, marginBottom: 4 },
  modalBtnRow: { flexDirection: 'row', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 },
  closeBtn: { alignSelf: 'center', marginTop: 10, paddingVertical: 6 },
});
